import { useState, useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import Navbar from '../components/Navbar'

const STEPS = [
  { label: 'Account', icon: '1' },
  { label: 'Security', icon: '2' },
  { label: 'Scanning', icon: '3' },
  { label: 'Results', icon: '4' },
]

export default function Register() {
  const [searchParams] = useSearchParams()
  const prefillEmail = searchParams.get('email') || ''
  const prefillName = searchParams.get('name') || ''

  const [email, setEmail] = useState(prefillEmail)
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const { register } = useAuth()

  // Store name for auto-scan after TFA setup
  useEffect(() => {
    if (prefillName) {
      localStorage.setItem('scan_subject_name', prefillName)
    }
    if (prefillEmail) {
      localStorage.setItem('scan_subject_email', prefillEmail)
    }
  }, [prefillName, prefillEmail])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')

    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    if (password.length < 12) {
      setError('Password must be at least 12 characters')
      return
    }

    setLoading(true)

    try {
      await register(email, password)
      // Redirect to TFA setup happens in the auth hook
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Registration failed. Try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen">
      <Navbar />

      <div className="pt-24 pb-16 px-4 flex flex-col items-center justify-center min-h-screen">
        {/* Step Progress Bar */}
        <div className="w-full max-w-lg mb-8">
          <div className="flex items-center justify-between">
            {STEPS.map((step, i) => (
              <div key={step.label} className="flex items-center flex-1 last:flex-initial">
                <div className="flex flex-col items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                    i === 0
                      ? 'bg-gradient-to-br from-ginger-400 to-orange-500 text-white shadow-lg shadow-ginger-500/40'
                      : 'bg-dark-700 text-dark-500'
                  }`}>
                    {step.icon}
                  </div>
                  <span className={`mt-2 text-xs font-medium ${
                    i === 0 ? 'text-ginger-400' : 'text-dark-500'
                  }`}>
                    {step.label}
                  </span>
                </div>
                {i < STEPS.length - 1 && (
                  <div className="flex-1 h-0.5 bg-dark-700 mx-3 mt-[-1rem]" />
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="card max-w-md w-full">
          <h1 className="text-2xl font-bold mb-2">Create your account</h1>
          <p className="text-dark-400 mb-6">
            {prefillName
              ? `Welcome, ${prefillName.split(' ')[0]}. Create your account to start your free exposure scan.`
              : "You'll need to set up two-factor authentication to access your reports."}
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="label">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
                required
              />
            </div>
            <div>
              <label className="label">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input"
                placeholder="At least 12 characters"
                required
              />
            </div>
            <div>
              <label className="label">Confirm Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="input"
                required
              />
            </div>

            {error && <p className="text-red-400 text-sm">{error}</p>}

            <button type="submit" disabled={loading} className="btn btn-primary w-full">
              {loading ? 'Creating account...' : 'Create Account'}
            </button>
          </form>

          <p className="text-center text-dark-400 text-sm mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-ginger-500 hover:underline">
              Sign in
            </Link>
          </p>

          <div className="mt-6 pt-6 border-t border-dark-700">
            <p className="text-dark-500 text-xs text-center">
              By creating an account, you agree to our{' '}
              <Link to="/terms" className="text-ginger-500 hover:underline">Terms of Service</Link>
              {' '}and{' '}
              <Link to="/privacy" className="text-ginger-500 hover:underline">Privacy Policy</Link>.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
